// DhanTracker.js - Main event tracking SDK
import { DEFAULTS, PRIORITY, getISTTime } from "./constants.js";
import { EncryptionManager } from "./utils/encryption.js";
import { getDeviceInfo, getBrowserInfo, getOSInfo, getNetworkType } from "./utils/deviceInfo.js";
import { RageDetectionManager } from "./utils/rageDetection.js";
import { RequestQueue } from "./utils/requestQueue.js";
import { AuthManager } from "./auth/authManager.js";
import { ErrorHandler, NetworkError } from "./utils/errorHandler.js";
import { UtmTracker } from "./utils/utmTracker.js";

class DhanTracker {
  constructor(config = {}) {
    this.validateConfig(config);
    this.initializeProperties(config);
    
    // Check if there's a pending buffer from a previous session in storage
    // and flush it before starting new activity.
    this.checkAndFlushPendingBuffer();

    // Recover any events that were persisted to localStorage during a previous
    // offline session (e.g. user closed tab while offline, then reopened later).
    this.recoverOfflineEvents();

    this.setupManagers();
    this.setupEventListeners();
    this.setupWebVitalsTracking();
    this.startFlushInterval();

    // Capture UTM / attribution params from the landing URL and fire a
    // one-time utm_capture event if new campaign params are present.
    this.captureUtm();
  }

  /**
   * Captures UTM / attribution parameters from the current URL.
   * Updates first-touch (set once) and last-touch (refreshed) attribution,
   * and fires a dedicated `utm_capture` event when new UTM params are detected.
   */
  captureUtm() {
    try {
      let result = null;

      // 1. Highest priority: an explicit landingUrl the app captured at its
      //    earliest entry point (before its router stripped the query string).
      if (this.landingUrl) {
        const search = this._extractSearch(this.landingUrl);
        result = this.utmTracker.capture(
          search,
          this.landingUrl,
          (typeof document !== 'undefined' && document.referrer) || ''
        );
      }

      // 2. Module-load-time snapshot (captured when the SDK script was parsed).
      if (!result) {
        result = this.utmTracker.captureFromSnapshot();
      }

      // 3. Live URL fallback (if params still present at init time).
      if (!result && typeof window !== 'undefined' && window.location) {
        result = this.utmTracker.capture(
          window.location.search,
          window.location.href,
          (typeof document !== 'undefined' && document.referrer) || ''
        );
      }

      if (this.debug) {
        try {
          console.log('[DhanTracker] UTM capture result:', result,
            '| landingUrl:', this.landingUrl || '(none)',
            '| live search:', (typeof window !== 'undefined' ? window.location.search : 'n/a'));
        } catch (e) {}
      }

      // No UTM params present — nothing to report.
      if (!result) return;

      const attribution = this.utmTracker.getAttribution();


      // Fire a dedicated attribution event (not attached to every batch).
      this.trackEvent('utm_capture', null, {
        ...result.params,
        is_first_touch: result.isNewFirstTouch,
        first_touch: attribution.first_touch,
        last_touch: attribution.last_touch,
      });
    } catch (e) {
      // UTM capture must never break the SDK
    }
  }

  /**
   * Extracts the query string (including "?") from a full URL.
   * @param {string} url - A full URL string.
   * @returns {string} The search portion, or "".
   */
  _extractSearch(url) {
    try {
      const idx = url.indexOf('?');
      if (idx === -1) return '';
      // Strip any hash fragment that follows the query string.
      const afterQ = url.slice(idx);
      const hashIdx = afterQ.indexOf('#');
      return hashIdx === -1 ? afterQ : afterQ.slice(0, hashIdx);
    } catch (e) {
      return '';
    }
  }

  validateConfig(config) {
    // As of v1.4.0 clientId is OPTIONAL. The SDK can initialize before login
    // (clientId unknown) and the clientId can be supplied later via a
    // getClientId callback or the setClientId() method. If none is ever
    // provided, an anonymous session-scoped user_id is used as the identifier.
    if (!config.apiEndpoint && !DEFAULTS.API_ENDPOINT) {
      throw new Error("API endpoint must be provided in config or config.js");
    }
  }

  initializeProperties(config) {
    const defaults = {
      appKey: "",
      appId: "",
      appInfo: DEFAULTS.APP_INFO,
      flushInterval: DEFAULTS.FLUSH_INTERVAL || 5000,
      library: DEFAULTS.LIBRARY,
      env: "production",
      source: "web",
      maxRetries: DEFAULTS.MAX_RETRIES,
      retryDelay: DEFAULTS.RETRY_DELAY,
      batchSize: DEFAULTS.BATCH_SIZE,
      autoRefresh: true,
      autoTrackErrors: true, // Automatically capture JS errors and unhandled rejections
      autoTrackWebVitals: true, // Enable automatic tracking of Core Web Vitals (LCP, CLS, INP, FCP, TTFB)
      ignoredErrors: ["undefined is not an object (evaluating 'window.__firefox__.reader')","Unhandled Runtime Error","Can't find variable: __firefox__","ResizeObserver loop completed with undelivered notifications","ResizeObserver loop limit exceeded"], // Array of error messages or RegExp patterns to exclude from tracking
      ignoredStacks: ["chrome-extension","script.hotjar.com"], // Array of stack trace patterns to exclude from tracking
      rageDetection: {
        enabled: true,
        rageClickThreshold: DEFAULTS.RAGE_CLICK_THRESHOLD,
        rageClickWindow: DEFAULTS.RAGE_CLICK_WINDOW,
        rageEventThreshold: DEFAULTS.RAGE_EVENT_THRESHOLD,
        rageEventWindow: DEFAULTS.RAGE_EVENT_WINDOW,
      },
      skipPatterns: [
        'AbortError',
        'Skipping view transition',
        'ResizeObserver loop',
        'NotAllowedError',
        'NavigationAborted',
        'signal is aborted',
        'operation was aborted'
      ],
      useWebSocket: false,
      webSocketEndpoint: "",
    };

    const settings = { ...defaults, ...config };

    // Core properties
    // Debug flag — when true, the SDK logs diagnostic info (e.g. UTM capture).
    this.debug = settings.debug === true;
    // Optional: the original landing URL captured by the app at its earliest
    // entry point. Use this when the app's router strips UTM query params
    // before the SDK loads. The app should read window.location.href at the
    // very first line of its bootstrap and pass it here.
    this.landingUrl = settings.landingUrl || null;
    // clientId is optional (v1.4.0+). It may be null at init (pre-login) and
    // supplied later via getClientId callback or setClientId().
    this.clientId = settings.clientId || null;
    // Optional callback the app provides to read its current clientId on demand.
    // Each app can fetch from its own storage (Redux, cookie, localStorage, etc.)
    // without the SDK needing to know the storage shape. Read at flush/auth time.
    this.getClientId = typeof settings.getClientId === 'function' ? settings.getClientId : null;
    // If an explicit userId is provided in config, it takes priority.
    // Otherwise a session-scoped user_id is generated/restored in
    // initializeSession() so it stays stable for the whole session.
    this.configUserId = settings.userId || null;
    this.userId = this.configUserId; // may be overwritten by session logic below

    // Storage keys scoped by appId (clientId may be absent, so don't key by it).
    const storageScope = settings.appId || settings.clientId || 'default';
    // sessionStorage key for pre-init event buffering
    this.preInitStorageKey = `dhan_preinit_events_${storageScope}`;
    // localStorage key scoped to appId so that different apps/domains using
    // the same SDK never share a session record with each other.
    // Intentionally NOT keyed by clientId — clientId changes on login (guest →
    // real user) and would cause the post-login init to miss the existing record.
    // Identity continuity is enforced inside the record via the userId field.
    const appId = settings.appId || 'default';
    this.localSessionKey = `dhan_session_${appId}`;
    // localStorage key for offline event persistence — survives tab/browser close
    this.offlineBufferKey = `dhan_offline_events_${appId}`;

    // UTM / attribution tracker (first-touch + last-touch)
    this.utmTracker = new UtmTracker(appId);

    // MUST be set before initializeSession() — it reads this.sessionTimeout to
    // decide whether a cached session is still valid. If unset, the comparison
    // `elapsed <= undefined` is false and a new session is wrongly created on
    // every init (e.g. every page refresh).
    this.sessionTimeout = settings.sessionTimeout || DEFAULTS.SESSION_TIMEOUT;

    this.sessionId = this.initializeSession();
    this.appId = settings.appId;
    this.appKey = settings.appKey;
    this.app = { ...settings.appInfo, app_id: this.appId };
    this.apiEndpoint = DEFAULTS.API_ENDPOINT;
    this.library = settings.library;

    // Configuration
    this.flushInterval = settings.flushInterval;
    this.env = settings.env;
    this.source = settings.source;
    this.maxRetries = settings.maxRetries;
    this.retryDelay = settings.retryDelay;
    this.batchSize = settings.batchSize;
    this.autoTrackErrors = settings.autoTrackErrors;
    this.autoTrackWebVitals = settings.autoTrackWebVitals;
    this.ignoredErrors = settings.ignoredErrors;
    this.ignoredStacks = settings.ignoredStacks;
    // sessionTimeout already set above (before initializeSession).
    // Patterns to skip - browser warnings and intentional aborts
    this.skipPatterns = settings.skipPatterns;

    // Device info
    this.device = getDeviceInfo();
    this.os = getOSInfo();
    this.browser = getBrowserInfo();

    // Runtime state
    this.buffer = [];
    // Buffer for events captured before auth token is ready
    this.preInitBuffer = [];
    this.globalMetadata = {};
    this.lastFlushTime = Date.now();
    this.sessionStart = Date.now();
    this.retryCount = 0;
    this.isOffline = false;

    // Load any events that were persisted before SDK was fully initialized
    this.loadPreInitEvents();

    // Web Vitals state - tracks Core Web Vitals metrics
    // LCP: Largest Contentful Paint - loading performance
    // CLS: Cumulative Layout Shift - visual stability
    // INP: Interaction to Next Paint - responsiveness
    // FCP: First Contentful Paint - initial render time
    // TTFB: Time to First Byte - server response time
    this.webVitals = {
      lcp: null,
      cls: 0,
      inp: null,
      fcp: null,
      ttfb: null,
      navigationType: null,
      finalized: false,
    };
    // Store PerformanceObserver instances for cleanup
    this.webVitalsObservers = {};
    // Bound event handlers for proper cleanup
    this._boundFinalizeWebVitals = null;
    this._boundVisibilityChangeFinalizeWebVitals = null;
    this._webVitalsFinalizeTimeoutId = null;

    this.defaultHeaders = { "Content-Type": "application/json" };
    this.rageDetectionConfig = settings.rageDetection;

    if (settings.useWebSocket && settings.webSocketEndpoint != "") {
      this.webSocket = new WebSocket(settings.webSocketEndpoint);
      this.webSocket.onopen = () => console.log('WebSocket connection established');
      this.webSocket.onerror = (error) => console.error('WebSocket error:', error);
    }
  }

  /**
   * Checks if the SDK is ready to send events to the server.
   * Events tracked before auth is ready are buffered in preInitBuffer.
   * @returns {boolean} True if auth manager has a valid token
   */
  isReadyToSend() {
    try {
      return !!(this.authManager && this.authManager.isTokenValid && this.authManager.isTokenValid());
    } catch (e) {
      return false;
    }
  }

  /**
   * Loads previously persisted pre-init events from session storage.
   * Called during SDK initialization to recover events from previous page loads
   * that occurred before the auth token was ready.
   */
  loadPreInitEvents() {
    try {
      const raw = sessionStorage.getItem(this.preInitStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.preInitBuffer = parsed;
      } else if (parsed && Array.isArray(parsed.events)) {
        // If the stored events belong to a different session, we shouldn't 
        // just move them to the memory buffer. They need to be pushed with original ID.
        if (parsed.sessionId !== this.sessionId) {
          this.checkAndFlushPendingBuffer();
        } else {
          this.preInitBuffer = parsed.events;
        }
      }
    } catch (e) {
      this.preInitBuffer = [];
    }
  }

  /**
   * Persists an event to session storage when SDK is not ready to send.
   * Maintains a maximum of 50 events to prevent storage overflow.
   * Events are recovered on next page load via loadPreInitEvents().
   * @param {Object} event - The event payload to persist
   */
  persistPreInitEvent(event) {
    try {
      this.preInitBuffer.push(event);
      if (this.preInitBuffer.length > 50) {
        this.preInitBuffer = this.preInitBuffer.slice(this.preInitBuffer.length - 50);
      }
      
      // Store events with current session context so they can be flushed 
      // with correct IDs later even if session changes.
      const payload = {
        sessionId: this.sessionId,
        userId: this.userId,
        events: this.preInitBuffer
      };
      sessionStorage.setItem(this.preInitStorageKey, JSON.stringify(payload));
    } catch (e) {
      // ignore
    }
  }
  
  /**
   * Checks for and flushes any pending events from previous sessions in storage.
   * Ensures guest events are pushed with guest IDs before user session starts.
   */
  async checkAndFlushPendingBuffer() {
    try {
      const raw = sessionStorage.getItem(this.preInitStorageKey);
      if (!raw) return;
      
      const parsed = JSON.parse(raw);
      // Skip if it's the old format or matches current session
      if (Array.isArray(parsed) || !parsed.sessionId || parsed.sessionId === this.sessionId) {
        return;
      }

      const { sessionId, userId, events } = parsed;
      if (!events || events.length === 0) return;

      // We need to wait for auth to be ready to flush.
      // Since this is called in constructor, we defer it.
      const flushOldSession = async () => {
        if (!this.isReadyToSend()) {
          // Retry later when ready
          setTimeout(flushOldSession, 2000);
          return;
        }

        try {
          await this.flush({
            overrideSessionId: sessionId,
            overrideUserId: userId,
            overrideEvents: events
          });
          // Only clear if flush succeeded (handled by flush's error logic)
          sessionStorage.removeItem(this.preInitStorageKey);
        } catch (e) {}
      };

      flushOldSession();
    } catch (e) {}
  }

  /**
   * Moves pre-init buffered events to the main buffer for sending.
   * Called after auth token is successfully initialized.
   * Respects MAX_BUFFER_SIZE to prevent memory issues.
   */
  drainPreInitEvents() {
    try {
      if (!this.preInitBuffer || this.preInitBuffer.length === 0) return;
      for (const evt of this.preInitBuffer) {
        if (this.buffer.length >= DEFAULTS.MAX_BUFFER_SIZE) break;
        this.buffer.push(evt);
      }
      this.preInitBuffer = [];
      sessionStorage.removeItem(this.preInitStorageKey);
    } catch (e) {
      // ignore
    }
  }

  // ─── Offline Event Persistence ─────────────────────────────────────────────

  /**
   * Persists the current in-memory buffer to localStorage so events survive
   * tab/browser close while offline.
   * Called on 'offline' event and on 'beforeunload' when offline.
   */
  persistOfflineEvents() {
    try {
      if (typeof localStorage === 'undefined') return;
      if (this.buffer.length === 0) return;

      // Read any existing offline events and merge
      let existing = [];
      const raw = localStorage.getItem(this.offlineBufferKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) existing = parsed;
      }

      const merged = [...existing, ...this.buffer];
      // Cap to prevent localStorage bloat
      const capped = merged.slice(-DEFAULTS.MAX_OFFLINE_BUFFER_SIZE);

      localStorage.setItem(this.offlineBufferKey, JSON.stringify(capped));
    } catch (e) {
      // localStorage full or unavailable — best-effort
    }
  }

  /**
   * Recovers events that were persisted to localStorage while offline.
   * Moves them into the in-memory buffer and clears localStorage.
   * Called when the SDK detects the network is back online.
   */
  recoverOfflineEvents() {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(this.offlineBufferKey);
      if (!raw) return;

      const events = JSON.parse(raw);
      if (!Array.isArray(events) || events.length === 0) return;

      for (const evt of events) {
        if (this.buffer.length >= DEFAULTS.MAX_BUFFER_SIZE) break;
        this.buffer.push(evt);
      }

      localStorage.removeItem(this.offlineBufferKey);
    } catch (e) {
      // Corrupt data — discard
      try { localStorage.removeItem(this.offlineBufferKey); } catch (_) {}
    }
  }

  setupManagers() {
    this.encryptionManager = new EncryptionManager();
    this.rageDetectionManager = new RageDetectionManager();
    this.requestQueue = new RequestQueue();
    
    // Set up error handler
    this.errorHandler = new ErrorHandler({
      maxRetries: this.maxRetries,
      baseDelay: this.retryDelay,
      onError: (error) => this.handleError(error)
    });
    
    // Set up rage detection manager with callback
    this.rageDetectionManager.onRageEvent = (rageEvent) => {
      this.buffer.push(rageEvent);
      if (this.buffer.length >= this.batchSize) {
        this.flush();
      }
    };
    
    // Create API client for auth manager
    this.apiClient = {
      makeRequest: this.makeRequest.bind(this)
    };
    
    this.authManager = new AuthManager(this, this.encryptionManager, this.apiClient);

    // Initialize auth token after 10s delay to allow main project APIs to complete first
    try {
      setTimeout(() => {
        this.authManager.initializeToken()
          .then(() => {
            // Auth ready - move buffered events to main buffer and flush
            this.drainPreInitEvents();
            this.flush();
          })
          .catch(() => {});
      }, 10000);
    } catch (e) {
      // ignore
    }

    if (this.rageDetectionConfig) {
      this.rageDetectionManager.configureRageDetection(this.rageDetectionConfig);
    }
  }

  /**
   * Sets up DOM and window event listeners for tracking user interactions,
   * navigation changes, and network status.
   */
  setupEventListeners() {
    // Update lastSeenAt in the cached session on page unload
    // If offline, persist events to localStorage so they survive tab close
    window.addEventListener("beforeunload", () => {
      try {
        const raw = localStorage.getItem(this.localSessionKey);
        if (raw) {
          const cached = JSON.parse(raw);
          cached.lastSeenAt = Date.now();
          localStorage.setItem(this.localSessionKey, JSON.stringify(cached));
        }
      } catch (e) {
        // ignore
      }

      // If offline, save buffer to localStorage before tab dies
      if (this.isOffline) {
        this.persistOfflineEvents();
      }
    });
    
    // Flush remaining events with keepalive before page unload.
    // Pass clearSession:false — a refresh/navigation is NOT a logout, so the
    // session must survive the reload and be reused on the next init.
    window.addEventListener("unload", () => {
      this.flush({ isUnloading: true });
      this.destroy({ clearSession: false });
    });



    document.addEventListener("click", (e) => {
      let t = e.target;
      while (t && t.nodeType === 1 && !t.hasAttribute("data-track")) {
        t = t.parentElement;
      }
      if (!t || t.nodeType !== 1 || !t.hasAttribute("data-track")) return;
      const name = t.getAttribute("data-track");
      const onclickAttr = t.getAttribute("onClick");
      this.trackEvent(name, null, onclickAttr);
    }, true);

    ["pushState", "replaceState"].forEach(m => {
      const orig = history[m];
      history[m] = function () {
        const rv = orig.apply(this, arguments);
        window.dispatchEvent(new Event("ma:navigation"));
        return rv;
      };
    });
    window.addEventListener("popstate", () => window.dispatchEvent(new Event("ma:navigation")));

    window.addEventListener('offline', () => {
      this.isOffline = true;
      // Persist in-memory buffer to localStorage so events survive tab close
      this.persistOfflineEvents();
    });
    window.addEventListener('online', async () => {
      this.isOffline = false;
      // Delay 10s to allow main project APIs to complete first
      setTimeout(async () => {
        try {
          await this.authManager.initializeToken();
        } catch (e) {
          // Will try again on next event/flush
        }
        // Recover offline-persisted events from localStorage
        this.recoverOfflineEvents();
        // Recover pre-init events and flush when back online
        this.drainPreInitEvents();
        this.flush();
      }, 10000);
    });

    // Set up automatic error tracking if enabled
    if (this.autoTrackErrors) {
      this.setupGlobalErrorTracking();
    }
  }

  /**
   * Sets up global error handlers to automatically capture:
   * - Uncaught JavaScript errors (window.onerror)
   * - Unhandled Promise rejections
   */
  setupGlobalErrorTracking() {
    // Capture uncaught JavaScript errors
    window.addEventListener("error", (event) => {
      this.trackError("JS_ERROR", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? {
          name: event.error.name,
          message: event.error.message,
          stack: event.error.stack
        } : null
      });
    });

    //! Warnings are getting logged too.
    // Capture unhandled Promise rejections (skip browser warnings/noise)
    // window.addEventListener("unhandledrejection", (event) => {
    //   const reason = event.reason;
      
    //   const reasonStr = reason?.message || reason?.name || String(reason);
    //   if (this.skipPatterns.some(p => reasonStr.includes(p))) {
    //     return;
    //   }
      
    //   this.trackError("UNHANDLED_PROMISE_REJECTION", {
    //     reason: reason instanceof Error ? {
    //       name: reason.name,
    //       message: reason.message,
    //       stack: reason.stack
    //     } : reason
    //   });
    // });
  }

  /**
   * Initializes Core Web Vitals tracking using PerformanceObserver API.
   * Tracks: LCP, CLS, INP, FCP, and TTFB metrics.
   * Metrics are finalized and sent when page becomes hidden or after 10s timeout.
   */
  setupWebVitalsTracking() {
    if (!this.autoTrackWebVitals) return;
    if (typeof window === 'undefined') return;
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      // Bind finalize handler for cleanup
      this._boundFinalizeWebVitals = this.finalizeWebVitals.bind(this);
      // Finalize metrics when page is hidden (most reliable for mobile)
      window.addEventListener('pagehide', this._boundFinalizeWebVitals, true);
      // Also handle visibility change for desktop browsers
      this._boundVisibilityChangeFinalizeWebVitals = () => {
        if (document.visibilityState === 'hidden') {
          this.finalizeWebVitals();
        }
      };
      document.addEventListener('visibilitychange', this._boundVisibilityChangeFinalizeWebVitals, true);

      // Schedule finalization after page load with 10s timeout as fallback
      const scheduleFinalize = () => {
        try {
          if (this.webVitals.finalized) return;
          if (this._webVitalsFinalizeTimeoutId) return;
          this._webVitalsFinalizeTimeoutId = setTimeout(() => {
            this._webVitalsFinalizeTimeoutId = null;
            this.finalizeWebVitals();
          }, 10000);
        } catch (e) {
          // ignore
        }
      };

      if (document.readyState === 'complete') {
        scheduleFinalize();
      } else {
        window.addEventListener('load', scheduleFinalize, { once: true });
      }

      // Extract TTFB from Navigation Timing API
      const nav = performance.getEntriesByType('navigation')?.[0];
      if (nav) {
        this.webVitals.ttfb = Math.max(0, nav.responseStart - nav.requestStart);
        this.webVitals.navigationType = nav.type || null;
      }

      // Set up individual metric observers
      this.setupFCPObserver();
      this.setupLCPObserver();
      this.setupCLSObserver();
      this.setupINPObserver();
    } catch (e) {
      // Ignore Web Vitals failures; SDK must never break the app
    }
  }

  /**
   * Sets up First Contentful Paint (FCP) observer.
   * FCP measures time from navigation to first content render.
   */
  setupFCPObserver() {
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            this.webVitals.fcp = entry.startTime;
          }
        }
      });
      po.observe({ type: 'paint', buffered: true });
      this.webVitalsObservers.fcp = po;
    } catch (e) {
      // ignore
    }
  }

  /**
   * Sets up Largest Contentful Paint (LCP) observer.
   * LCP measures loading performance - time to render largest content element.
   * Uses the last entry as LCP can update until user interaction.
   */
  setupLCPObserver() {
    try {
      const po = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) {
          this.webVitals.lcp = last.startTime;
        }
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
      this.webVitalsObservers.lcp = po;
    } catch (e) {
      // ignore
    }
  }

  /**
   * Sets up Cumulative Layout Shift (CLS) observer.
   * CLS measures visual stability - sum of unexpected layout shifts.
   * Excludes shifts caused by user input (hadRecentInput).
   */
  setupCLSObserver() {
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            this.webVitals.cls += entry.value;
          }
        }
      });
      po.observe({ type: 'layout-shift', buffered: true });
      this.webVitalsObservers.cls = po;
    } catch (e) {
      // ignore
    }
  }

  /**
   * Sets up Interaction to Next Paint (INP) observer.
   * INP measures responsiveness - worst interaction latency.
   * Uses Event Timing API with 40ms threshold to filter noise.
   */
  setupINPObserver() {
    try {
      const po = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // INP is approximated by the max interaction latency from Event Timing entries
          // duration is in milliseconds
          const duration = entry.duration;
          if (!Number.isFinite(duration)) continue;
          if (this.webVitals.inp === null || duration > this.webVitals.inp) {
            this.webVitals.inp = duration;
          }
        }
      });
      po.observe({ type: 'event', buffered: true, durationThreshold: 40 });
      this.webVitalsObservers.inp = po;
    } catch (e) {
      // ignore
    }
  }

  /**
   * Finalizes and sends Web Vitals metrics.
   * Called when page becomes hidden or after timeout.
   * Disconnects all observers and sends collected metrics as a single event.
   */
  finalizeWebVitals() {
    if (!this.autoTrackWebVitals) return;
    if (this.webVitals.finalized) return;
    this.webVitals.finalized = true;

    try {
      if (this._webVitalsFinalizeTimeoutId) {
        clearTimeout(this._webVitalsFinalizeTimeoutId);
        this._webVitalsFinalizeTimeoutId = null;
      }
    } catch (e) {
      // ignore
    }

    try {
      // Take final LCP reading before disconnect
      if (this.webVitalsObservers.lcp?.takeRecords) this.webVitalsObservers.lcp.takeRecords();
    } catch (e) {
      // ignore
    }

    try {
      // Disconnect all observers to prevent memory leaks
      Object.values(this.webVitalsObservers).forEach((po) => {
        try { po.disconnect(); } catch (e) {}
      });
      this.webVitalsObservers = {};
    } catch (e) {
      // ignore
    }

    // Build metrics payload with common context
    const common = {
      navigation_type: this.webVitals.navigationType,
      page_visibility: document.visibilityState,
    };

    // Only include metrics that were successfully captured
    const metrics = {};
    if (Number.isFinite(this.webVitals.ttfb)) metrics.ttfb_ms = this.webVitals.ttfb;
    if (Number.isFinite(this.webVitals.fcp)) metrics.fcp_ms = this.webVitals.fcp;
    if (Number.isFinite(this.webVitals.lcp)) metrics.lcp_ms = this.webVitals.lcp;
    if (Number.isFinite(this.webVitals.inp)) metrics.inp_ms = this.webVitals.inp;
    if (Number.isFinite(this.webVitals.cls)) metrics.cls = this.webVitals.cls;

    if (Object.keys(metrics).length > 0) {
      // Send web vitals as a tracked event
      this.trackEvent('web_vital', null, {
        ...common,
        metrics,
      });

      // Use keepalive to ensure delivery during page unload
      this.flush({ isUnloading: true });
    }
  }

  startFlushInterval() {
    this.flushIntervalId = setInterval(() => this.flush(), this.flushInterval);
  }

  // Core event tracking
  trackEvent(eventType, context = null, properties = {}, metadataOverrides = {}) {
    if (!eventType) {
      throw new Error("Event type is required");
    }

    // Refresh session activity timestamp (throttled) so the idle timeout
    // reflects real usage, not just SDK init.
    this.touchSession();
    
    if (typeof this.referrer === "undefined") {
      this.referrer = document.referrer;
    }
    
    const currentPath = window.location.pathname;
    const currentUrl = window.location.href;
    const previousPath = this.referrer;
    this.referrer = currentPath;

    const sanitizedProperties = this.sanitizeProperties(properties);
    const now = new Date();

    // Track event for rage detection
    this.rageDetectionManager.trackRageEvent(eventType, sanitizedProperties);

    // Check if this is a rage event
    const rageInfo = this.rageDetectionManager.getRageEventInfo(eventType, sanitizedProperties);
    const isRage = rageInfo && rageInfo.isRage;

    if (this.buffer.length >= DEFAULTS.MAX_BUFFER_SIZE) {
      // console.warn("Event buffer is full, event will be dropped");
      return false;
    }

    // Regular events are added to buffer
    if (!isRage) {
      const eventPayload = {
        event_id: "evt_" + now.getTime(),
        event_type: eventType,
        timestamp: getISTTime(),
        context: {
          page: {
            path: currentPath,
            referrer: previousPath,
            title: document.title,
            url: currentUrl,
          },
        },
        properties: sanitizedProperties,
        metadata: {
          client_sent_at: getISTTime(),
          received_at: null,
          processing_time_ms: null,
          library: this.library,
          ...this.globalMetadata,
          ...metadataOverrides,
        },
      };

      if (!this.isReadyToSend()) {
        // Auth not ready - persist event for later sending
        this.persistPreInitEvent(eventPayload);
        return true;
      }

      this.buffer.push(eventPayload);

      if (this.buffer.length >= this.batchSize) {
        this.flush();
      }

      return true;
    }
  }

/**
 * Sanitizes properties to prevent XSS attacks.
 * Escapes HTML special characters and recursively handles nested objects.
 * Properly handles Error objects by using getOwnPropertyNames.
 * @param {Object} properties - Properties to sanitize
 * @returns {Object} Sanitized properties object
 */
sanitizeProperties(properties) {
  if (!properties || typeof properties !== 'object') return {};
  
  const sanitized = {};
  // Error objects need getOwnPropertyNames to access non-enumerable properties
  const isError = properties instanceof Error || (properties && properties.stack && properties.message);
  const keys = isError 
    ? Object.getOwnPropertyNames(properties) 
    : Object.keys(properties);
  for (const key of keys) {
    const value = properties[key];
    if (typeof value === "string") {
      // Escape HTML special characters to prevent XSS
      sanitized[key] = value.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    } else if (value && typeof value === "object") {
      // Recursive sanitization for nested objects and errors
      sanitized[key] = this.sanitizeProperties(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

  // Event flushing
  /**
   * Flushes buffered events to the server.
   * Uses request queue to prevent duplicate sends and handles offline scenarios.
   * @param {Object} options - Flush options
   * @param {boolean} options.isUnloading - If true, uses keepalive for page unload
   */
  async flush(options = {}) {
    const { 
      isUnloading = false, 
      overrideSessionId = null, 
      overrideUserId = null, 
      overrideEvents = null,
      overrideClientId = null
    } = options;

    const eventsToFlush = overrideEvents || this.buffer;
    if (eventsToFlush.length === 0) return;

    // Snapshot the clientId NOW (at flush-request time), so even if clientId
    // changes before the queued send runs, this batch keeps the correct id.
    const clientIdForBatch = overrideClientId !== null ? overrideClientId : this.resolveClientId();

    // Use the serial requestQueue to handle flushes.
    return this.requestQueue.add(async () => {
      // Prepare batch from events
      const maxBatchSize = this.batchSize;
      const eventsToSend = overrideEvents ? eventsToFlush : eventsToFlush.slice(0, maxBatchSize);
      
      const now = new Date();
      // Lightweight attribution reference on every batch (hybrid model):
      // gives per-batch resilience without the full UTM object. The detailed
      // attribution lives in the one-time utm_capture event.
      const attributionRef = this.utmTracker ? this.utmTracker.getAttributionRef() : null;
      const payload = {
        batch_id: "batch_" + now.getTime(),
        client_id: clientIdForBatch,
        user_id: overrideUserId || this.userId,
        session_id: overrideSessionId || this.sessionId,
        app_id: this.appId,
        ip: "",
        network_type: getNetworkType(),
        events: eventsToSend,
        attribution_ref: attributionRef,
        metadata: {
          library: this.library,
          source: this.source,
          env: this.env,
          batch_created_at: getISTTime(),
          sdk_version: this.library.version,
        },
        device: getDeviceInfo(),
        os: getOSInfo(),
        app: this.app,
        browser: getBrowserInfo(),
      };

      // Send via WebSocket if available and connected, otherwise use HTTP
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify(payload));
        if (!overrideEvents) this.buffer = this.buffer.slice(eventsToSend.length);
      } else {
        try {
          await this.errorHandler.handleNetworkRequest(async () => {
            const newToken = await this.authManager.getValidToken();
            const headers = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
            };

            const fetchOptions = {
              method: "POST",
              headers,
              body: JSON.stringify(payload),
              keepalive: isUnloading
            };

            const response = await fetch(this.apiEndpoint + "/api/activity", fetchOptions);

            if (response.status === 401 && this.authManager.authConfig.refreshToken) {
              const newToken = await this.authManager.refreshToken();
              const retryResponse = await fetch(this.apiEndpoint + "/api/activity", {
                ...fetchOptions,
                headers: { ...headers, Authorization: `Bearer ${newToken}` }
              });
              if (!retryResponse.ok) throw new Error(`HTTP error! status: ${retryResponse.status}`);
            } else if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          }, 'event_flush');

          if (!overrideEvents) {
            this.buffer = this.buffer.slice(eventsToSend.length);
          }
          this.retryCount = 0;
        } catch (error) {
          if (error.isNetworkError && error.code === 'OFFLINE') return;
          if (!overrideEvents) this.buffer = this.buffer.slice(eventsToSend.length);
        }
      }
    }, isUnloading ? PRIORITY.HIGH : PRIORITY.NORMAL);
  }

  // API requests
  async makeRequest(url, method = "GET", data = null, useAuth = true, header = {}) {
    return await this.errorHandler.handleNetworkRequest(async () => {
      const headers = { ...this.defaultHeaders, ...header };

      if (useAuth) {
        const token = await this.authManager.getValidToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }

      const config = {
        method,
        headers: {
          ...headers,
          // Add CORS headers for cross-origin requests
          'X-Requested-With': 'XMLHttpRequest',
          'Origin': window.location.origin
        },
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'same-origin' // Include cookies for same-origin requests
      };

      const response = await fetch(`${this.apiEndpoint}${url}`, config);

      if (response.status === 401 && useAuth) {
        try {
          const newToken = await this.authManager.refreshToken();
          headers["Authorization"] = `Bearer ${newToken}`;
        } catch (refreshError) {
          throw new Error(`Token refresh failed: ${refreshError.message}`);
        }

        const retryResponse = await fetch(`${this.apiEndpoint}${url}`, {
          ...config,
          headers: {
            ...headers,
            'X-Requested-With': 'XMLHttpRequest',
            'Origin': window.location.origin

          },
          credentials: 'same-origin'
        });

        if (!retryResponse.ok) {
          throw new Error(`API request failed with status ${retryResponse.status}`);
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      return response.json();
    }, `api_request_${method}_${url}`);
  }

  // Rage detection methods
  configureRageDetection(config) {
    this.rageDetectionManager.configureRageDetection(config);
  }

  getRageDetectionStats() {
    return this.rageDetectionManager.getRageDetectionStats();
  }

  getRageEventInfo(eventType, properties) {
    return this.rageDetectionManager.getRageEventInfo(eventType, properties);
  }

  /**
   * Tracks an error event with optional severity level.
   * Supports filtering via ignoredErrors config (strings or RegExp patterns).
   * @param {string} errorType - Type of error (e.g., 'JS_ERROR', 'UNHANDLED_PROMISE_REJECTION')
   * @param {Object|Error} errorData - Error details or Error object
   * @param {string} severity - Error severity level ('error', 'warning', 'info')
   */
  trackError(errorType, errorData, severity = 'error') {
    const message = errorData.message || (errorData.reason && (errorData.reason.message || errorData.reason)) || "";
    
    // Check if error stack should be ignored based on ignoredStacks config
    const stack = errorData.stack || (errorData.error && errorData.error.stack) || "";
    if (this.ignoredStacks && this.ignoredStacks.length > 0) {
      const shouldIgnoreStack = this.ignoredStacks.some(pattern => {
        if (pattern instanceof RegExp) {
          return pattern.test(stack);
        }
        return stack.includes(pattern);
      });
      
      if (shouldIgnoreStack) return;
    }
    
    // Check if error should be ignored based on ignoredErrors config
    if (this.ignoredErrors && this.ignoredErrors.length > 0) {
      const shouldIgnore = this.ignoredErrors.some(pattern => {
        if (pattern instanceof RegExp) {
          return pattern.test(message);
        }
        return message.includes(pattern);
      });
      
      if (shouldIgnore) return;
    }

    const now = new Date();
    const currentPath = window.location.pathname;
    const currentUrl = window.location.href;

    // Build error event payload with full context
    const errorEvent = {
        event_id: "err_" + now.getTime(),
        event_type: "sdk_error_report",
        timestamp: getISTTime(),
        context: {
          page: {
            path: currentPath,
            title: document.title,
            url: currentUrl,
          },
        },
        properties: {
          error_type: errorType,
          severity: severity,
          // Handle both Error objects and plain error data objects
          ...(errorData instanceof Error 
            ? {
                message: errorData.message,
                stack: errorData.stack,
                name: errorData.name,
                ...this.sanitizeProperties(errorData)
              }
            : this.sanitizeProperties(errorData))
        },
        metadata: {
          client_sent_at: getISTTime(),
          library: this.library,
          ...this.globalMetadata,
          is_error_event: true
        },
    };

    this.buffer.push(errorEvent);

    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }

    // Also call internal error handler for logging/debugging if needed
    this.handleError(errorData);
  }

  // Error handling
  handleError(error) {
    if (this.debug) {
      // console.error('DhanTracker Error:', error);
    }
    
    // Emit error event for external handling
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('dhantracker:error', {
        detail: {
          error,
          timestamp: getISTTime(),
          context: 'network_failure'
        }
      }));
    }
  }

  getErrorStats() {
    return this.errorHandler.getErrorStats();
  }

  // Session management
  /**
   * Restores an existing session from localStorage if it is still valid,
   * otherwise creates a new one.
   *
   * Also manages a session-scoped user_id:
   *   - A new user_id is generated when a new session is created.
   *   - The same user_id is reused for the lifetime of the session.
   *   - An explicit config.userId always takes priority over the generated one.
   *
   * Session record shape stored in localStorage:
   * {
   *   sessionId : string,
   *   userId    : string,   // session-scoped generated user id
   *   createdAt : number,   // ms epoch
   *   lastSeenAt: number,   // ms epoch — refreshed on SDK init, on each tracked event (throttled), and on page unload
   * }
   *
   * A cached session is reused when:
   *   1. A record exists in localStorage.
   *   2. The time since lastSeenAt is within sessionTimeout (24 hours).
   *   3. cached.sessionId is present and non-empty.
   *
   * The session (and its user_id) persists across login/logout transitions so
   * the backend can stitch guest and authenticated activity using shared IDs.
   */
  initializeSession() {
    try {
      if (typeof localStorage === 'undefined') {
        // No persistence available — generate ephemeral IDs for this instance
        this.userId = this.configUserId || this.generateUserId();
        return this.generateSessionId();
      }

      const raw = localStorage.getItem(this.localSessionKey);

      if (this.debug) {
        try {
          console.log('[DhanTracker] initializeSession read',
            '| key:', this.localSessionKey,
            '| raw:', raw,
            '| sessionTimeout:', this.sessionTimeout);
        } catch (e) {}
      }

      if (raw) {
        const cached = JSON.parse(raw);
        const now = Date.now();
        const elapsed = now - (cached.lastSeenAt || cached.createdAt || 0);
        const withinTimeout = elapsed <= this.sessionTimeout;

        if (this.debug) {
          try {
            console.log('[DhanTracker] session reuse check',
              '| cached.sessionId:', cached.sessionId,
              '| elapsed(ms):', elapsed,
              '| withinTimeout:', withinTimeout);
          } catch (e) {}
        }

        if (withinTimeout && cached.sessionId) {
          // Valid existing session — refresh lastSeenAt and reuse.
          // Reuse the session-scoped userId unless an explicit config userId
          // was provided (which always wins).
          this.userId = this.configUserId || cached.userId || this.generateUserId();
          cached.lastSeenAt = now;
          cached.userId = this.userId;
          localStorage.setItem(this.localSessionKey, JSON.stringify(cached));
          return cached.sessionId;
        }
      }
    } catch (e) {
      if (this.debug) {
        try { console.log('[DhanTracker] initializeSession error:', e); } catch (_) {}
      }
      // Corrupt or inaccessible localStorage — fall through to create a new session
    }

    if (this.debug) {
      try { console.log('[DhanTracker] initializeSession → creating NEW session (no reuse)'); } catch (e) {}
    }
    return this.rotateSession();
  }

  /**
   * Creates a new session (and a new session-scoped user_id), persists it to
   * localStorage, and returns the new session ID.
   */
  rotateSession() {
    const newSessionId = this.generateSessionId();
    // Generate a fresh user_id for the new session (unless config provides one)
    this.userId = this.configUserId || this.generateUserId();
    const now = Date.now();

    try {
      if (typeof localStorage !== 'undefined') {
        const record = {
          sessionId : newSessionId,
          userId    : this.userId,
          createdAt : now,
          lastSeenAt: now,
        };
        localStorage.setItem(this.localSessionKey, JSON.stringify(record));
      }
    } catch (e) {
      // localStorage unavailable — session still works in-memory
    }

    return newSessionId;
  }

  /**
   * Updates the session's lastSeenAt timestamp to reflect recent activity.
   * Called on every tracked event so the idle-timeout reflects real usage,
   * not just SDK init. Throttled (default 15s) to avoid hammering localStorage
   * on high-frequency events.
   */
  touchSession() {
    try {
      if (typeof localStorage === 'undefined') return;
      const now = Date.now();

      // Throttle writes — only persist at most once per throttle window.
      if (this._lastTouchAt && (now - this._lastTouchAt) < DEFAULTS.SESSION_TOUCH_THROTTLE) {
        return;
      }

      const raw = localStorage.getItem(this.localSessionKey);
      let cached = raw ? JSON.parse(raw) : null;

      // If no record exists (e.g. cleared mid-session), recreate one so the
      // current in-memory session is persisted rather than silently dropped.
      if (!cached || !cached.sessionId) {
        cached = {
          sessionId: this.sessionId,
          userId: this.userId,
          createdAt: now,
          lastSeenAt: now,
        };
      } else {
        cached.lastSeenAt = now;
        // Keep the record in sync with the in-memory identity.
        cached.sessionId = this.sessionId;
        cached.userId = this.userId;
      }

      localStorage.setItem(this.localSessionKey, JSON.stringify(cached));
      // Only advance the throttle marker AFTER a successful write.
      this._lastTouchAt = now;
    } catch (e) {
      // Non-critical — ignore
    }
  }

  // Utility methods
  generateSessionId() {
    // clientId may be null pre-login; fall back to userId then a constant.
    const scope = this.clientId || this.userId || 'anon';
    return "sess_" + Date.now() + "_" + scope;
  }

  /**
   * Generates a unique, session-scoped user id.
   */
  generateUserId() {
    const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, '')
      : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    return "usr_" + rand;
  }

  /**
   * Resolves the current clientId at send time.
   * Priority: getClientId callback → stored clientId → empty string.
   * This lets pre-login batches carry "" and post-login batches carry the
   * real id without rebuilding the SDK or rotating the session.
   * @returns {string} The current clientId, or "" if unknown.
   */
  resolveClientId() {
    try {
      if (this.getClientId) {
        const id = this.getClientId();
        if (id) return id;
      }
    } catch (e) {
      // getClientId threw — fall through
    }
    return this.clientId || "";
  }

  /**
   * Explicitly sets the clientId (e.g. after login).
   * Flushes any buffered pre-login events FIRST (with their original anonymous
   * clientId) and waits for that flush to complete before switching the
   * clientId, so pre-login events are never retagged with the new identity.
   * @param {string} clientId - The now-known client identifier.
   * @returns {Promise<void>}
   */
  async setClientId(clientId) {
    if (!clientId || clientId === this.clientId) return;

    // Snapshot the current (pre-login) clientId and flush the buffered events
    // under that identity. We await so the switch happens only after send.
    const previousClientId = this.resolveClientId();
    try {
      await this.flush({ overrideClientId: previousClientId });
    } catch (e) {
      // If flush fails, events remain buffered; they'll go out with the new id.
    }

    this.clientId = clientId;
    // Keep auth manager in sync so the next auth/init call sends the real id.
    try {
      if (this.authManager && this.authManager.config) {
        this.authManager.config.clientId = clientId;
      }
    } catch (e) {}
  }

  /**
   * Tears down the SDK.
   * @param {Object} [options]
   * @param {boolean} [options.clearSession=true] - When true, the session is
   *   removed from localStorage so the next init starts fresh. Use true on an
   *   explicit LOGOUT. On page unload/refresh pass false so the session
   *   survives the reload (a refresh is NOT a logout).
   */
  destroy(options = {}) {
    const { clearSession = true } = options;

    this.authManager.cleanup();
    clearInterval(this.flushIntervalId);
    // Finalize and send any pending Web Vitals before cleanup
    try {
      this.finalizeWebVitals();
    } catch (e) {
      // ignore
    }
    // Only clear the session on an explicit logout — NOT on page unload/refresh.
    if (clearSession) {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(this.localSessionKey);
        }
      } catch (e) {
        // ignore
      }
    }
    this.cleanup();
    this.requestQueue = new RequestQueue();
    window.removeEventListener("unload", this.cleanup);
  }

  /**
   * Cleans up all SDK resources including timers, observers, and event listeners.
   * Called during destroy() and page unload.
   */
  cleanup() {
    clearInterval(this.flushIntervalId);
    this.rageDetectionManager.cleanup();
    this.buffer = [];
    this.globalMetadata = {};
    // Clean up Web Vitals observers and event listeners
    try {
      if (this._webVitalsFinalizeTimeoutId) {
        clearTimeout(this._webVitalsFinalizeTimeoutId);
        this._webVitalsFinalizeTimeoutId = null;
      }
      if (this._boundFinalizeWebVitals) {
        window.removeEventListener('pagehide', this._boundFinalizeWebVitals, true);
      }
      if (this._boundVisibilityChangeFinalizeWebVitals) {
        document.removeEventListener('visibilitychange', this._boundVisibilityChangeFinalizeWebVitals, true);
      }
    } catch (e) {
      // ignore
    }
    sessionStorage.removeItem("tabId");
  }
}

export default DhanTracker;